// Perfil público: a parte que vem da gamificação (nível, divisão, ofensiva, números, conquistas, liga, trios e lances),
// igual para quem olha, e o "Contra você", que depende de quem olha. A API junta com os campos da conta (apelido,
// rosto, plano, cidade com consentimento) e monta a resposta pela lista fechada de campos.
import type { Badge, NivelBadge } from './tipos.ts';
import type { EstadoDivisao } from './divisoes.ts';

// Os lances que o perfil mostra, nesta ordem.
export const LANCES_DO_PERFIL = ['zap_no_doze', 'virada', 'blefe', 'peito_de_aco', 'mao_de_ferro'] as const;
export type LanceDoPerfil = (typeof LANCES_DO_PERFIL)[number]; // os nomes ficam em NOME_DO_LANCE (lances.ts)

export interface BadgeNoPerfil { id: string; nome: string; emoji: string; nivel: NivelBadge; categoria: Badge['categoria'] }
export interface TrioNoPerfil {
  id: string;
  nome: string;
  escudo: string;
  membros: { usuario: string; apelido: string; avatar: string | null }[];
  capitao: boolean; // o dono do perfil é o capitão
  partidas: number;
  aproveitamento: number; // vitórias / partidas, de 0 a 100
}
export interface LigaNoPerfil {
  pontosTemporada: number;
  posicaoSemana: { posicao: number; escopo: 'cidade' | 'brasil' } | null; // na cidade, com o consentimento; senão, no Brasil
  vezesTop10: number; // semanas em que entrou num Top 10 (cidade, estado ou Brasil)
  melhorPosicaoTemporada: number | null; // a melhor posição num Top 10 da semana nesta temporada
}
export interface PerfilNaGamificacao {
  nivel: number;
  divisao: EstadoDivisao;
  ofensiva: number;
  partidas: number;
  vitorias: number;
  badges: BadgeNoPerfil[]; // os ganhos, o de maior nível primeiro
  liga: LigaNoPerfil;
  trios: TrioNoPerfil[];
  trioPrincipal: { id: string; nome: string; escudo: string } | null; // o de mais partidas
  lances: Partial<Record<LanceDoPerfil, number>>; // só os que já fez
  coroa: boolean; // fechou a semana passada em 1º no Rei do Pote (cidade, estado ou Brasil)
}
export interface Confronto {
  dono: number; // vitórias do dono do perfil nas partidas em lados opostos
  visitante: number;
  ultimas: { partida: string; terminadaEm: number; venceu: boolean; placar: [number, number] }[]; // do dono primeiro
}

const ORDEM_DO_NIVEL: Record<NivelBadge, number> = { diamante: 3, ouro: 2, prata: 1, bronze: 0 };
// Os badges ganhos, o de maior nível primeiro; no empate, a ordem do catálogo. Um por badge: o nível mais alto.
export function badgesDoPerfil(ganhos: readonly (Badge & { ganho: unknown })[]): BadgeNoPerfil[] {
  const melhor = new Map<string, Badge>();
  for (const b of ganhos) {
    if (!b.ganho) continue;
    const a = melhor.get(b.id);
    if (!a || ORDEM_DO_NIVEL[b.nivel] > ORDEM_DO_NIVEL[a.nivel]) melhor.set(b.id, b);
  }
  return [...melhor.values()]
    .sort((a, b) => ORDEM_DO_NIVEL[b.nivel] - ORDEM_DO_NIVEL[a.nivel] || a.ordem - b.ordem)
    .map((b) => ({ id: b.id, nome: b.nome, emoji: b.emoji, nivel: b.nivel, categoria: b.categoria }));
}

export function lancesDoPerfil(est: Partial<Record<string, number>>): Partial<Record<LanceDoPerfil, number>> {
  const out: Partial<Record<LanceDoPerfil, number>> = {};
  for (const l of LANCES_DO_PERFIL) if ((est[l] ?? 0) > 0) out[l] = est[l]!;
  return out;
}

export const CONFRONTOS_NA_TELA = 5;
